import {
	isValidRepurchasePercentage,
	StoredRepurchaseCoupon,
	useActivationOnLoginRequested,
	useRepurchaseCouponActions,
	useStoredRepurchaseCoupon,
} from '@/data/Content/RepurchaseCouponState';
import { useDppMemberPromotion } from '@/data/Content/DppMemberPromotion';
import { personalSizesFromDpp, useRecomPersonalSizes } from '@/data/Content/RecomPersonalSizes';
import { useServerSideRuntime } from '@/data/context/origin';
import {
	DPP_JOURNEY_ID_PATTERN,
	DPP_JOURNEY_QUERY_KEY,
	DPP_MEMBER_ID_PATTERN,
} from '@/data/dpp/constants';
import type { DppMemberPromotion } from '@/data/dpp/types';
import { useUser } from '@/data/User';
import { GetServerSidePropsContext } from 'next';
import { ParsedUrlQuery } from 'querystring';
import { useEffect, useMemo } from 'react';

export const PERSONALIZATION_QUERY_KEYS = {
	promotionId: 'dpp_id',
	percentage: 'dpp_percentage',
	partNumbers: 'dpp_partnumbers',
	firstName: 'dpp_name',
	expiration: 'dpp_exp',
	member: 'dpp_member',
} as const;

export const PERSONALIZATION_LANDING_PATH = 'descubre-tu-cupon';
export const PERSONALIZATION_LOGIN_ORIGIN = 'go_to_personalization';
const PERSONALIZATION_LOGIN_PATH = '/iniciar-sesion';

export const PERSONALIZATION_URL_KEYS = [
	...Object.values(PERSONALIZATION_QUERY_KEYS),
	DPP_JOURNEY_QUERY_KEY,
];

export const PERSONALIZATION_CAROUSEL_ESPOT = 'DPSV2PersonalizationCarousel_{prefix}';
export const REPURCHASE_EXCLUSIONS_ESPOT = 'DPSV2CouponExclusions_{prefix}';

const PREFIX_TOKEN = '{prefix}';

export const toPersonalizationEspot = (template: string, percentage: number) =>
	template.replaceAll(PREFIX_TOKEN, `${percentage}OFF`);

export type PersonalizationOpeningOrder = {
	orderId?: string;
	placedAt?: string;
	partNumbers: string[];
};

export interface PersonalizationParams {
	promotionId: string;
	percentage?: number;
	partNumbers: string[];
	firstName?: string;
	expirationDateTime?: string;
	member?: string;
	journeyId?: string;
	openingOrder?: PersonalizationOpeningOrder;
	sizes?: DppMemberPromotion['sizes'];
}

type RepurchaseCouponStatus = 'AVAILABLE' | 'APPLIED';

export type RepurchaseCoupon = PersonalizationParams & {
	percentage: number;
	exclusions: string;
	status: RepurchaseCouponStatus;
};

const PROMOTION_ID_PATTERN = /^[\p{L}\p{N}%._ -]{1,128}$/u;
const PART_NUMBER_PATTERN = /^[\w-]{1,40}$/;
const FIRST_NAME_PATTERN = /^[\p{L}\p{M}'. -]{1,40}$/u;
const MAX_PART_NUMBERS = 7;
export const REPURCHASE_EXCLUSIONS = 'No aplica con otros descuentos, jerseys ni electrónicos.';

const queryValue = (context: GetServerSidePropsContext, key: string) => {
	const value = context.query[key];
	return (Array.isArray(value) ? value[0] : value)?.trim();
};

const parsePartNumbers = (value?: string) =>
	(value ?? '')
		.split(',')
		.map((partNumber) => partNumber.trim())
		.filter((partNumber) => PART_NUMBER_PATTERN.test(partNumber))
		.slice(0, MAX_PART_NUMBERS);

const parsePercentage = (value?: string) => {
	const percentage = Number(value);
	return isValidRepurchasePercentage(percentage) ? percentage : undefined;
};

const parseExpiration = (value?: string) => {
	const time = new Date((value ?? '').replace(/ (\d{2}:\d{2})$/, '+$1')).getTime();
	return Number.isNaN(time) ? undefined : new Date(time).toISOString();
};

const PERSONALIZATION_COOKIE = 'dpp_personalization';

type ReadValue = (key: string) => string | undefined;

const readMatching = (read: ReadValue, key: string, pattern: RegExp) => {
	const value = read(key);
	return value && pattern.test(value) ? value : undefined;
};

const withoutUndefined = <T extends Record<string, unknown>>(values: T) =>
	Object.fromEntries(
		Object.entries(values).filter(([, value]) => value !== undefined)
	) as Partial<T>;

const parsePersonalization = (read: ReadValue): PersonalizationParams | null => {
	const promotionId = read(PERSONALIZATION_QUERY_KEYS.promotionId);
	if (!promotionId || !PROMOTION_ID_PATTERN.test(promotionId)) {
		return null;
	}

	const expirationDateTime = parseExpiration(read(PERSONALIZATION_QUERY_KEYS.expiration));
	if (expirationDateTime && new Date(expirationDateTime).getTime() < Date.now()) {
		return null;
	}

	return {
		promotionId,
		partNumbers: parsePartNumbers(read(PERSONALIZATION_QUERY_KEYS.partNumbers)),
		...withoutUndefined({
			percentage: parsePercentage(read(PERSONALIZATION_QUERY_KEYS.percentage)),
			firstName: readMatching(read, PERSONALIZATION_QUERY_KEYS.firstName, FIRST_NAME_PATTERN),
			expirationDateTime,
			member: readMatching(read, PERSONALIZATION_QUERY_KEYS.member, DPP_MEMBER_ID_PATTERN),
			journeyId: readMatching(read, DPP_JOURNEY_QUERY_KEY, DPP_JOURNEY_ID_PATTERN),
		}),
	};
};

const getPagePath = (context: GetServerSidePropsContext) =>
	(context.resolvedUrl ?? '').split('?')[0];

const appendSetCookie = (context: GetServerSidePropsContext, cookie: string) => {
	const current = context.res.getHeader('Set-Cookie');
	const cookies = Array.isArray(current) ? current : current ? [String(current)] : [];
	context.res.setHeader('Set-Cookie', [...cookies, cookie]);
};

const storePersonalization = (
	context: GetServerSidePropsContext,
	params: PersonalizationParams
) => {
	const value = JSON.stringify({
		path: getPagePath(context),
		[PERSONALIZATION_QUERY_KEYS.promotionId]: params.promotionId,
		[PERSONALIZATION_QUERY_KEYS.percentage]: params.percentage?.toString(),
		[PERSONALIZATION_QUERY_KEYS.partNumbers]: params.partNumbers.join(','),
		[PERSONALIZATION_QUERY_KEYS.firstName]: params.firstName,
		[PERSONALIZATION_QUERY_KEYS.expiration]: params.expirationDateTime,
		[PERSONALIZATION_QUERY_KEYS.member]: params.member,
		[DPP_JOURNEY_QUERY_KEY]: params.journeyId,
	});
	appendSetCookie(
		context,
		`${PERSONALIZATION_COOKIE}=${encodeURIComponent(value)}; Path=/; HttpOnly; Secure; SameSite=Lax`
	);
};

const readStoredPersonalization = (context: GetServerSidePropsContext) => {
	try {
		const raw = context.req.cookies[PERSONALIZATION_COOKIE];
		const stored: Record<string, unknown> | undefined = raw ? JSON.parse(raw) : undefined;
		if (!stored || stored.path !== getPagePath(context)) {
			return null;
		}
		return parsePersonalization((key) => {
			const value = stored[key];
			return typeof value === 'string' ? value.trim() : undefined;
		});
	} catch {
		return null;
	}
};

export const getPersonalizationParams = (
	context: GetServerSidePropsContext
): PersonalizationParams | null => {
	if (!queryValue(context, PERSONALIZATION_QUERY_KEYS.promotionId)) {
		return readStoredPersonalization(context);
	}
	const params = parsePersonalization((key) => queryValue(context, key));
	if (params) {
		storePersonalization(context, params);
	}
	return params;
};

export const PERSONALIZATION_FALLBACK_PATH = '/';
const LOGIN_RESERVED_QUERY_KEYS = ['path', 'origin', 'flow'];
const PERSONALIZATION_QUERY_VALUES: string[] = Object.values(PERSONALIZATION_QUERY_KEYS);

const toLoginQuery = (query: ParsedUrlQuery) => {
	const params = new URLSearchParams();
	Object.entries(query)
		.filter(
			([key]) =>
				!LOGIN_RESERVED_QUERY_KEYS.includes(key) && !PERSONALIZATION_QUERY_VALUES.includes(key)
		)
		.forEach(([key, value]) =>
			[value]
				.flat()
				.filter((item): item is string => item !== undefined)
				.forEach((item) => params.append(key, item))
		);
	params.set('origin', PERSONALIZATION_LOGIN_ORIGIN);
	return params.toString();
};

export const getPersonalizationRedirect = ({
	identifier,
	personalization,
	isLoggedIn,
	query = {},
}: {
	identifier?: string;
	personalization: PersonalizationParams | null;
	isLoggedIn?: boolean;
	query?: ParsedUrlQuery;
}) => {
	if (identifier !== PERSONALIZATION_LANDING_PATH) {
		return undefined;
	}
	if (!personalization) {
		return PERSONALIZATION_FALLBACK_PATH;
	}
	return personalization.member || isLoggedIn
		? undefined
		: `${PERSONALIZATION_LOGIN_PATH}?${toLoginQuery(query)}`;
};

export const usePersonalizationPromotionId = () =>
	useServerSideRuntime().personalization?.promotionId;

export const usePersonalizationPercentage = () =>
	useServerSideRuntime().personalization?.percentage;

const toValidDateTime = (value?: string | null) =>
	value && !Number.isNaN(Date.parse(value)) ? value : undefined;

const useCouponExpiration = (stored?: StoredRepurchaseCoupon) => {
	const { personalization } = useServerSideRuntime();
	const { promotion } = useDppMemberPromotion();
	const dppExpiration =
		promotion && promotion.promotionName === personalization?.promotionId
			? promotion.eligibleUntil
			: undefined;
	return (
		toValidDateTime(stored?.expirationDateTime) ??
		toValidDateTime(dppExpiration) ??
		personalization?.expirationDateTime
	);
};

export const usePersonalizationExpiration = () => useCouponExpiration(useStoredRepurchaseCoupon());

export const usePersonalizationSeed = () => {
	const { personalization } = useServerSideRuntime();
	const { promotion, loading } = useDppMemberPromotion();
	const openingOrder = personalization?.openingOrder ?? promotion?.openingOrder;
	return {
		partNumber: openingOrder?.partNumbers?.at(0),
		orderId: openingOrder?.orderId,
		placedAt: openingOrder?.placedAt,
		loading: !personalization?.openingOrder && loading,
	};
};

export const usePersonalizationDppSizes = () => {
	const { personalization } = useServerSideRuntime();
	const { promotion, loading } = useDppMemberPromotion();
	return {
		dppSizes: personalization?.sizes ?? promotion?.sizes,
		loading: !personalization?.sizes && loading,
	};
};

export const usePersonalizationSizes = () => {
	const { dppSizes, loading: loadingDppSizes } = usePersonalizationDppSizes();
	const { sizes: profileSizes, loading: loadingProfile } = useRecomPersonalSizes();
	const sizes = useMemo(() => {
		const merged = { ...profileSizes, ...personalSizesFromDpp(dppSizes) };
		return Object.keys(merged).length ? merged : undefined;
	}, [profileSizes, dppSizes]);
	return {
		sizes,
		loading: loadingDppSizes || loadingProfile,
	};
};

export const useRepurchaseCoupon = () => {
	const { personalization } = useServerSideRuntime();
	const { user } = useUser();
	const stored = useStoredRepurchaseCoupon();
	const { registerCoupon, applyCoupon } = useRepurchaseCouponActions();
	const promotionId = personalization?.promotionId;
	const expirationDateTime = personalization?.expirationDateTime;
	const percentage = personalization?.percentage ?? 0;
	const couponExpiration = useCouponExpiration(stored);

	useEffect(() => {
		if (promotionId && percentage) {
			registerCoupon({ promotionId, percentage, expirationDateTime });
		}
	}, [promotionId, percentage, expirationDateTime, registerCoupon]);

	const isLoggedIn = !!user?.isLoggedIn;
	const activatingOnLogin = useActivationOnLoginRequested() && isLoggedIn;
	const status: RepurchaseCouponStatus =
		activatingOnLogin || (isLoggedIn && !stored) ? 'APPLIED' : stored?.status ?? 'AVAILABLE';
	const redeemable = !isLoggedIn || !!stored;

	const coupon = useMemo<RepurchaseCoupon | undefined>(() => {
		if (!personalization || !percentage) {
			return undefined;
		}
		return {
			...personalization,
			percentage,
			expirationDateTime: couponExpiration,
			exclusions: REPURCHASE_EXCLUSIONS,
			status,
		};
	}, [personalization, percentage, couponExpiration, status]);

	return { coupon, redeemable, applyCoupon };
};
